import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { type SortOption } from "@/config/apiClient.types";
import {
  type AvailablePageLength,
  DEFAULT_PAGE_LENGTH,
} from "@/constants/catalogMediumDisplayed";
import { selectedClientServiceIdAtom } from "@/store";
import {
  CatalogApi,
  CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum,
  CatalogControllerGetPaginatedServiceCatalogSortEnum,
} from "../../config/apiClient";
import { createApiClient } from "../../config/createApiClient";

type CatalogSortEnumValue =
  (typeof CatalogControllerGetPaginatedServiceCatalogSortEnum)[keyof typeof CatalogControllerGetPaginatedServiceCatalogSortEnum];
type CatalogNbPerPageEnumValue =
  (typeof CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum)[keyof typeof CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum];

interface QueryGetCatalog {
  currentPage: number;
  sort: SortOption;
  search?: string;
  nbPerPage?: AvailablePageLength;
  categoriesIds: Array<string>;
}

const apiClient = createApiClient(CatalogApi);

export const useCatalog = () => {
  const selectedClientServiceIdAtomValue = useAtomValue(
    selectedClientServiceIdAtom
  );

  const useGetCatalog = ({
    currentPage,
    sort,
    search = "",
    nbPerPage = DEFAULT_PAGE_LENGTH,
    categoriesIds,
  }: QueryGetCatalog) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: [
        "catalog",
        currentPage,
        sort,
        search,
        nbPerPage,
        selectedClientServiceIdAtomValue,
        categoriesIds,
      ],
      enabled: selectedClientServiceIdAtomValue > 0,
      queryFn: () => {
        type QueryParams = Omit<
          Parameters<
            typeof apiClient.catalogControllerGetPaginatedServiceCatalog
          >["0"],
          "clientServiceId"
        >;

        const sortMap: Record<SortOption, CatalogSortEnumValue> = {
          default: CatalogControllerGetPaginatedServiceCatalogSortEnum.Default,
          alphabeticalASC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalAsc,
          alphabeticalDESC:
            CatalogControllerGetPaginatedServiceCatalogSortEnum.AlphabeticalDesc,
        };

        const nbPerPageMap: Record<
          AvailablePageLength,
          CatalogNbPerPageEnumValue
        > = {
          10: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_10,
          20: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_20,
          50: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_50,
          100: CatalogControllerGetPaginatedServiceCatalogNbPerPageEnum.NUMBER_100,
        };

        const queryParams: QueryParams = {
          page: String(currentPage),
          sort: sortMap[sort],
          search,
          nbPerPage: nbPerPageMap[nbPerPage],
          categoriesIds: categoriesIds.join(";"),
        };

        const cleanQueryParams = Object.fromEntries(
          Object.entries(queryParams).filter(
            ([_, value]) =>
              typeof value === "number" ||
              (typeof value === "string" && value.trim() !== "") ||
              (Array.isArray(value) && value.length > 0)
          )
        );

        return apiClient.catalogControllerGetPaginatedServiceCatalog({
          clientServiceId: selectedClientServiceIdAtomValue,
          ...cleanQueryParams,
        });
      },
    });

  const useGetCatalogMedium = (id: string) =>
    useQuery({
      staleTime: 30 * 60 * 1000,
      queryKey: ["catalogMedium", id, selectedClientServiceIdAtomValue],
      queryFn: () =>
        apiClient.catalogControllerGetCatalogMedium({
          clientServiceId: selectedClientServiceIdAtomValue,
          catalogMediumId: id,
        }),
      enabled: selectedClientServiceIdAtomValue > 0 && !!id,
    });

  const useCreateOrUpdateEcoScore = () => {
    const selectedClientServiceIdAtomValue = useAtomValue(
      selectedClientServiceIdAtom
    );
    const queryClient = useQueryClient();

    return useMutation({
      mutationKey: ["createOrUpdateEcoScore"],
      mutationFn: ({
        catalogMediumId,
        updateEcoScoreCommand,
      }: {
        catalogMediumId: string;
        updateEcoScoreCommand: Parameters<
          typeof apiClient.catalogControllerUpdateCatalogMediumEcoScore
        >[0]["updateEcoScoreCommand"];
      }) =>
        apiClient.catalogControllerUpdateCatalogMediumEcoScore({
          clientServiceId: selectedClientServiceIdAtomValue,
          catalogMediumId,
          updateEcoScoreCommand,
        }),
      onSuccess: () => queryClient.invalidateQueries({ queryKey: ["catalog"] }),
    });
  };

  return { useGetCatalog, useGetCatalogMedium, useCreateOrUpdateEcoScore };
};
